const PARA_ID_ATTR_PATTERN = /\s(?:w14:|ns\d:|w:)paraId="[^"]*"/g;
const WHITE_TEXT_COLOR = '<w:color w:val="FFFFFF"/>';

const MARKUP_COMPATIBILITY_NS =
  'http://schemas.openxmlformats.org/markup-compatibility/2006';
const WORD_2010_WORDML_NS = 'http://schemas.microsoft.com/office/word/2010/wordml';
const OFFICE_DOCUMENT_RELATIONSHIPS_NS =
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

const AUTO_GENERATED_NAMESPACE_PREFIXES = {
  ns1: { uri: MARKUP_COMPATIBILITY_NS, canonical: 'mc' },
  ns2: { uri: WORD_2010_WORDML_NS, canonical: 'w14' },
  ns3: { uri: OFFICE_DOCUMENT_RELATIONSHIPS_NS, canonical: 'r' },
};

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function fixDocumentXmlNamespaces(xml) {
  let normalized = String(xml ?? '');

  for (const [autoPrefix, { uri, canonical }] of Object.entries(
    AUTO_GENERATED_NAMESPACE_PREFIXES
  )) {
    normalized = normalized.replace(
      new RegExp(`xmlns:${autoPrefix}="${escapeRegExp(uri)}"`, 'g'),
      `xmlns:${canonical}="${uri}"`
    );
    normalized = normalized.replace(
      new RegExp(`\\b${autoPrefix}:`, 'g'),
      `${canonical}:`
    );
  }

  normalized = normalized.replace(
    /(<w:document\b[^>]*\s)mc:Ignorable="[^"]*"/,
    '$1mc:Ignorable="w14"'
  );

  return normalized;
}

export function validateDocumentXmlNamespaces(xml) {
  const rootMatch = String(xml ?? '').match(/<w:document\b([^>]*)>/);
  if (!rootMatch) {
    return { valid: false, reason: 'missing w:document root' };
  }

  const rootAttrs = rootMatch[1];
  if (/xmlns:ns\d+=/.test(rootAttrs)) {
    return { valid: false, reason: 'auto-generated xmlns:nsN remains on root' };
  }

  const declaredPrefixes = new Set(
    [...rootAttrs.matchAll(/xmlns:([A-Za-z0-9]+)=/g)].map((match) => match[1])
  );
  const ignorableMatch = rootAttrs.match(/mc:Ignorable="([^"]*)"/);
  if (!ignorableMatch) {
    return { valid: true };
  }

  for (const prefix of ignorableMatch[1].split(/\s+/).filter(Boolean)) {
    if (!declaredPrefixes.has(prefix)) {
      return {
        valid: false,
        reason: `mc:Ignorable prefix "${prefix}" is not declared on root`,
      };
    }
  }

  return { valid: true };
}

const BLACK_LABEL_CELL_PATTERN =
  /<w:tc>\s*<w:tcPr>[\s\S]*?<w:shd[^>]*w:fill="000000"[\s\S]*?<\/w:tcPr>[\s\S]*?<\/w:tc>/g;

function findTableCellEnd(xml, startIndex) {
  if (!xml.startsWith('<w:tc>', startIndex)) {
    return -1;
  }
  let depth = 0;
  let index = startIndex;
  while (index < xml.length) {
    if (xml.startsWith('<w:tc>', index)) {
      depth += 1;
      index += 6;
      continue;
    }
    if (xml.startsWith('</w:tc>', index)) {
      depth -= 1;
      index += 7;
      if (depth === 0) {
        return index;
      }
      continue;
    }
    index += 1;
  }
  return -1;
}

function dedupeWordParagraphIds(xml) {
  const seenParaIds = new Set();
  return xml.replace(PARA_ID_ATTR_PATTERN, (match) => {
    const id = match.match(/="([^"]+)"/)?.[1];
    if (!id || seenParaIds.has(id)) {
      return '';
    }
    seenParaIds.add(id);
    return match;
  });
}

function addWhiteTextColorToRunProperties(xml) {
  let updated = xml.replace(/<w:rPr>([\s\S]*?)<\/w:rPr>/g, (match, inner) => {
    if (inner.includes('<w:color')) {
      return match;
    }
    return `<w:rPr>${WHITE_TEXT_COLOR}${inner}</w:rPr>`;
  });
  updated = updated.replace(
    /<w:r>((?!<w:rPr>)[\s\S])*?<\/w:r>/g,
    (run) =>
      run.includes('<w:rPr>')
        ? run
        : run.replace('<w:r>', `<w:r><w:rPr>${WHITE_TEXT_COLOR}</w:rPr>`)
  );
  return updated;
}

function fixBlackSectionLabelTextColor(xml) {
  return xml.replace(BLACK_LABEL_CELL_PATTERN, (cell) =>
    addWhiteTextColorToRunProperties(cell)
  );
}

function stripWhiteTextColorFromNonLabelCells(xml) {
  let result = '';
  let index = 0;
  while (index < xml.length) {
    const cellStart = xml.indexOf('<w:tc>', index);
    if (cellStart === -1) {
      result += xml.slice(index);
      break;
    }
    result += xml.slice(index, cellStart);
    const cellEnd = findTableCellEnd(xml, cellStart);
    if (cellEnd === -1) {
      result += xml.slice(cellStart);
      break;
    }
    const cell = xml.slice(cellStart, cellEnd);
    const isLabelCell =
      /^<w:tc>\s*<w:tcPr>[\s\S]*?<w:shd[^>]*w:fill="000000"/.test(cell);
    result += isLabelCell
      ? cell
      : cell.replace(/<w:color w:val="FFFFFF"\/>/g, '');
    index = cellEnd;
  }
  return result;
}

function disableRightToLeftParagraphDefaults(xml) {
  return xml
    .replace(/<w:bidi\s*\/>/g, '<w:bidi w:val="0"/>')
    .replace(
      /<w:pPr>((?!<w:bidi)[\s\S])*?<\/w:pPr>/g,
      (block) =>
        block.includes('<w:bidi')
          ? block
          : block.replace('<w:pPr>', '<w:pPr><w:bidi w:val="0"/>')
    );
}

export function normalizeTermSheetDocumentXml(xml) {
  let normalized = String(xml ?? '');
  normalized = fixDocumentXmlNamespaces(normalized);
  normalized = normalized.replace(/<w:rtlGutter\s*\/>/g, '');
  normalized = disableRightToLeftParagraphDefaults(normalized);
  normalized = dedupeWordParagraphIds(normalized);
  normalized = normalized.replace(/<w:color w:val="FFFFFF"\/>/g, '');
  normalized = fixBlackSectionLabelTextColor(normalized);
  normalized = stripWhiteTextColorFromNonLabelCells(normalized);
  return normalized;
}

export function normalizeTermSheetStylesXml(xml) {
  return String(xml ?? '')
    .replace(/he-IL/g, 'en-US')
    .replace(
      /<w:pPrDefault><w:pPr><w:bidi\s*\/>/g,
      '<w:pPrDefault><w:pPr><w:bidi w:val="0"/>'
    );
}

export function countDuplicateWordParagraphIds(xml) {
  const ids = [...String(xml ?? '').matchAll(PARA_ID_ATTR_PATTERN)].map(
    (match) => match[0].match(/="([^"]+)"/)?.[1]
  );
  const seen = new Set();
  let duplicates = 0;
  for (const id of ids) {
    if (!id) {
      continue;
    }
    if (seen.has(id)) {
      duplicates += 1;
    }
    seen.add(id);
  }
  return duplicates;
}

export async function normalizeTermSheetDocxZip(zip) {
  const documentPath = 'word/document.xml';
  const stylesPath = 'word/styles.xml';
  const documentXml = await zip.file(documentPath)?.async('string');
  if (documentXml) {
    zip.file(documentPath, normalizeTermSheetDocumentXml(documentXml), {
      createFolders: false,
    });
  }
  const stylesXml = await zip.file(stylesPath)?.async('string');
  if (stylesXml) {
    zip.file(stylesPath, normalizeTermSheetStylesXml(stylesXml), {
      createFolders: false,
    });
  }
  return zip;
}
